import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type TechnicianLicense = components['schemas']['TechnicianLicenseResponse'];
export type TechnicianLicenseRequest = components['schemas']['TechnicianLicenseRequest'];
export type TechnicianLicenseSummary = components['schemas']['TechnicianLicenseSummaryResponse'];

/**
 * The licence classes the API accepts, as a value the UI can iterate to build
 * a `<select>`. `satisfies` pins each entry to a real member of the generated
 * union, so a typo or a REMOVED class is a type error.
 *
 * ⚠️ It does NOT catch a class ADDED on the API side: a subset satisfies
 * `readonly Union[]` perfectly well, so a new member compiles green here and
 * simply never appears in the dropdown. Getting that guarantee needs an
 * exhaustiveness map (`Record<TechnicianLicenseRequest['class'], string>`),
 * which fails on a missing key — worth doing if this list ever grows past the
 * AC's fixed six.
 */
export const LICENSE_CLASSES = [
  'Apprentice',
  'Journeyman',
  'Master',
  'GasFitter',
  'BackflowTester',
  'Other',
] as const satisfies readonly TechnicianLicenseRequest['class'][];

export const LICENSE_STATUSES = [
  'Active',
  'Suspended',
  'Revoked',
] as const satisfies readonly TechnicianLicenseRequest['status'][];

export type LicenseClass = (typeof LICENSE_CLASSES)[number];
export type LicenseStatus = (typeof LICENSE_STATUSES)[number];

/** Thrown for the 403 a technician gets when looking at someone ELSE's licences. */
export class TechnicianLicensesNotPermittedError extends Error {
  constructor() {
    super('Not permitted to view these licences');
    this.name = 'TechnicianLicensesNotPermittedError';
  }
}

function licensesQueryKey(profileId: string) {
  return ['technicianProfiles', profileId, 'licenses'] as const;
}

/**
 * One technician's licences (M1-37). The route is bare-authenticated on the
 * API side with an ownership check in the handler, so — unlike
 * {@link useTechnicianProfileByUser} — a Technician CAN call this for their
 * own profile and there is no second "/me" variant to branch onto. The 403 is
 * still reachable (someone else's profile), and gets its own error type so the
 * page can render a refusal rather than a generic failure.
 *
 * Takes a profile id, not a user id: the licences hang off the PROFILE, which
 * the page has already loaded by the time this runs — hence the `enabled`
 * gate, since a technician with no profile yet has no licences to ask for.
 */
export function useTechnicianLicenses(profileId: string | undefined) {
  return useQuery({
    queryKey: licensesQueryKey(profileId ?? ''),
    enabled: profileId !== undefined,
    queryFn: async () => {
      const { data, response } = await apiClient.GET(
        '/api/v1/technician-profiles/{profileId}/licenses',
        { params: { path: { profileId: profileId! } } },
      );
      if (response.status === 403) {
        throw new TechnicianLicensesNotPermittedError();
      }
      if (!response.ok || !data) {
        throw new Error(`GET .../licenses failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

/**
 * Every licence mutation invalidates the PROFILE as well as the licence list —
 * the profile response carries the badge counts (`licenses`), so a renewal
 * that only refreshed the list would leave a stale "1 expired" badge sitting
 * above a list that no longer has one.
 */
function invalidate(queryClient: ReturnType<typeof useQueryClient>, profileId: string) {
  void queryClient.invalidateQueries({ queryKey: licensesQueryKey(profileId) });
  void queryClient.invalidateQueries({ queryKey: ['technicianProfiles'] });
}

export function useCreateTechnicianLicense(profileId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: TechnicianLicenseRequest) => {
      const { data, response } = await apiClient.POST(
        '/api/v1/technician-profiles/{profileId}/licenses',
        { params: { path: { profileId } }, body: request },
      );
      if (!response.ok || !data) {
        throw new Error(`POST .../licenses failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidate(queryClient, profileId),
  });
}

export function useUpdateTechnicianLicense(profileId: string, licenseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: TechnicianLicenseRequest) => {
      const { data, response } = await apiClient.PUT(
        '/api/v1/technician-profiles/{profileId}/licenses/{licenseId}',
        { params: { path: { profileId, licenseId } }, body: request },
      );
      if (!response.ok || !data) {
        throw new Error(`PUT .../licenses/{licenseId} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidate(queryClient, profileId),
  });
}

export function useDeleteTechnicianLicense(profileId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (licenseId: string) => {
      const { response } = await apiClient.DELETE(
        '/api/v1/technician-profiles/{profileId}/licenses/{licenseId}',
        { params: { path: { profileId, licenseId } } },
      );
      // 204, so there is no body to check — `!data` would reject the success case.
      if (!response.ok) {
        throw new Error(`DELETE .../licenses/{licenseId} failed: ${response.status}`);
      }
    },
    onSuccess: () => invalidate(queryClient, profileId),
  });
}

const ALLOWED_DOCUMENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Uploads a licence's document photo — request a SAS, PUT straight to Blob
 * Storage, confirm. Identical in shape to `useUploadTechnicianPhoto`, because
 * it IS the same single-slot flow: one document per licence row, overwritten
 * on re-upload, not a queue (api/CONVENTIONS.md's M1-37 section).
 */
export function useUploadLicenseDocument(profileId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ licenseId, file }: { licenseId: string; file: File }) => {
      if (!ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
        throw new Error(`Unsupported file type: ${file.type}`);
      }

      const requested = await apiClient.POST(
        '/api/v1/technician-profiles/{profileId}/licenses/{licenseId}/document',
        { params: { path: { profileId, licenseId } }, body: { contentType: file.type } },
      );
      if (!requested.response.ok || !requested.data) {
        throw new Error(`request-upload failed: ${requested.response.status}`);
      }

      const uploaded = await fetch(requested.data.uploadUrl, {
        method: 'PUT',
        headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type },
        body: file,
      });
      if (!uploaded.ok) {
        throw new Error(`blob upload failed: ${uploaded.status}`);
      }

      const completed = await apiClient.POST(
        '/api/v1/technician-profiles/{profileId}/licenses/{licenseId}/document/complete',
        { params: { path: { profileId, licenseId } } },
      );
      if (!completed.response.ok || !completed.data) {
        throw new Error(`complete failed: ${completed.response.status}`);
      }
      return completed.data;
    },
    onSuccess: () => invalidate(queryClient, profileId),
  });
}

/**
 * Every technician profile in the account, indexed by user id — what the
 * roster needs to badge each row (M1-37).
 *
 * `users.read`-gated, exactly like the `/api/v1/users` request the roster
 * already makes, so a caller who can see the roster at all can see this too
 * and there is no partial-permission case to handle. Returns an empty map
 * rather than throwing when it is not permitted: a missing badge is a
 * degraded roster, not a broken one, and `/people` is unreachable without
 * `users.read` in the first place.
 */
export function useTechnicianProfilesByUserId(enabled = true) {
  return useQuery({
    queryKey: ['technicianProfiles', 'byUserId', 'all'],
    enabled,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/technician-profiles');
      if (!response.ok || !data) {
        return new Map<string, TechnicianLicenseSummary>();
      }
      return new Map(data.map((profile) => [profile.userId, profile.licenses]));
    },
    retry: false,
  });
}
